import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon, Skeleton, StatusBadge, type IconName, type StatusKind } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { useWorkflowSetup } from "../use-workflow-setup";
import { useRefs } from "./refs";
import { describe, isOpen, type SetupRow, type SetupStatus } from "./setup-model";

const S = "engage.workflows.setup";

/** Status = icon + text + colour: "done" is never just green. */
const BADGE: Record<SetupStatus, { status: StatusKind; icon: IconName }> = {
  done: { status: "success", icon: "check-circle" },
  todo: { status: "danger", icon: "alert-circle" },
  warn: { status: "warning", icon: "alert-triangle" },
};

function RowView({ row }: { row: SetupRow }) {
  const { t } = useTranslation();
  const badge = BADGE[row.status];
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-3" data-testid="setup-item" data-status={row.status}>
      <StatusBadge className="shrink-0 self-start" status={badge.status} icon={badge.icon} label={t(`${S}.status.${row.status}`)} />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-sm">{row.text}</p>
        {row.steps.length > 0 ? (
          <ol className="list-decimal space-y-1 pl-5 text-xs text-fg-muted">
            {row.steps.map((step) => <li key={step}>{step}</li>)}
          </ol>
        ) : null}
        {row.fix ? (
          <AppLink className="inline-flex min-h-6 items-center gap-1 text-xs font-bold text-primary-ink underline" href={row.fix.href}>
            {row.fix.label}
            <Icon name="arrow-right" className="size-3" />
          </AppLink>
        ) : null}
      </div>
    </li>
  );
}

interface SetupChecklistProps {
  workflowId: string;
  workflowName: string;
}

/** "Finish setting up": what still stands between the saved draft and a workflow that runs - published, started by a Home option, the things its steps point at. */
export function SetupChecklist({ workflowId, workflowName }: SetupChecklistProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const fallbackAgentId = useRefs().agents[0]?.id;
  const setup = useWorkflowSetup(workflowId);
  if (setup.isPending) return <Skeleton className="mx-auto h-24 w-full max-w-3xl rounded-lg" />;
  if (!setup.data) return null;
  const items = setup.data.items;
  const context = { slug: summary.slug, workflowName, fallbackAgentId };
  const rows = items.map((item, index) => describe(item, index, context, t));
  const left = items.filter(isOpen).length;
  if (left === 0) {
    return (
      <details className="mx-auto w-full max-w-3xl rounded-lg border border-line bg-surface p-4" data-testid="setup-checklist-done">
        <summary className="flex cursor-pointer items-center gap-2 text-sm font-bold">
          <Icon name="check-circle" className="size-4 text-success" />
          {t(`${S}.allDone`)}
        </summary>
        <ul className="mt-2 divide-y divide-line" aria-label={t(`${S}.title`)}>{rows.map((row) => <RowView key={row.id} row={row} />)}</ul>
      </details>
    );
  }
  return (
    <section className="mx-auto w-full max-w-3xl rounded-lg border border-line bg-surface p-4" aria-labelledby="workflow-setup-title" data-testid="setup-checklist">
      <h2 id="workflow-setup-title" className="font-bold">{t(`${S}.title`)}</h2>
      <p className="text-xs text-fg-muted">{t(`${S}.subtitle`, { count: left })}</p>
      <ul className="mt-2 divide-y divide-line">{rows.map((row) => <RowView key={row.id} row={row} />)}</ul>
    </section>
  );
}
